import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Paper,
  Box,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  useTheme,
  useMediaQuery
} from "@mui/material";

import {
  Radar as RadarIcon,
  NotificationsActive as BellOnIcon,
  NotificationsOff as BellOffIcon,
  Warning as WarningIcon,
  LocalFireDepartment as FireIcon,
  AccessAlarm as AlarmIcon,
  CheckCircle as OkIcon,
  ArrowForward as ArrowForwardIcon,
  HourglassEmpty as GhostIcon,
  PersonAdd as PersonAddIcon
} from "@mui/icons-material";

import { motion, AnimatePresence } from "framer-motion";
import { doc, updateDoc, arrayUnion } from "firebase/firestore";
import { db } from "../../firebase";
import useAgencyWorker from "../../hooks/useAgencyWorker";

/* =======================
   BRAND THEME
======================= */
const BRAND = {
  green: "#1B8F5A",
  greenSoft: "#E6F4EE",
  greenDark: "#0F5C3A",
  yellowHot: "#FFC400",
  white: "#FFFFFF",
  textMuted: "#6B7280",
  border: "#E5E7EB"
};

export default function AICoachWidget({
  inquiries = [],
  hotLeads = [],
  user = {},
  onAlertClick, // This function should handle the navigation (navigate(`/inquiries/${id}`))
  onAssignClick
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const canHandleAlertClick = typeof onAlertClick === "function";
  const canHandleAssignClick = typeof onAssignClick === "function";


  /* =======================
      STATE
  ======================= */
  //Safe local storage check

  const [isMuted, setIsMuted] = useState(() => {
    try {
      if (typeof window === "undefined") return false;
      return localStorage.getItem("agency_mute") === "true";
    } catch (e) {
      return false;
    }
  });

  /* =======================
      WORKER & LOOP FIX
  ======================= */
  const { alerts, updateData } = useAgencyWorker({
    workerPath: "/agency.worker.js",
    session: { role: user.role, userId: user.id || user.uid }
  });

  // [FIX] Prevent infinite refresh loop by tracking data string
  const prevDataRef = useRef("59");

  useEffect(() => {
    const currentDataStr = JSON.stringify({ 
      i: inquiries.map(i => i.id), 
      h: hotLeads.map(h => h.id) 
    });

    if (prevDataRef.current !== currentDataStr) {
        updateData({ inquiries, leads: hotLeads });
        prevDataRef.current = currentDataStr;
    }
  }, [inquiries, hotLeads, updateData]);

  /* =======================
      INTERACTIONS
  ======================= */
  const markAsViewed = async (alert) => {
    if (!user?.uid) return;
    let rawId = alert.id.replace(/^(new_|hot_|urgent_|ghost_|lead_|opportunity_)/g, "");
    if (!rawId) return;

    try {
      const ref = doc(db, "allInquiries", rawId);
      updateDoc(ref, { viewers: arrayUnion(user.uid) });
    } catch {
      /* silent */
    }
  };

  const handleAlertClick = (alert) => {
    // 1. Mark as viewed in Firebase
    markAsViewed(alert);
    
    // 2. Extract clean ID for navigation
    const cleanId = alert.id.replace(/^(new_|hot_|urgent_|ghost_|lead_|opportunity_)/g, "");
    
    // 3. Navigate (Parent component handles the routing)
    if (canHandleAlertClick && cleanId) {
        // We pass a modified alert object with the clean ID to ensure the router goes to the right place
        onAlertClick({ ...alert, id: cleanId }); 
    }
  };

  /* =======================
      NOTIFICATIONS (Mobile Safe)
  ======================= */
  useEffect(() => {
    if (alerts.length === 0 || isMuted) return;

    const critical = alerts.find(
      (a) => a.severity === "critical" || a.severity === "high"
    );

    if (critical && typeof window !== "undefined" && "Notification" in window) {
       if (Notification.permission === "granted") {
         try {
           const n = new Notification("New Priority Alert", {
             body: critical.message,
             tag: critical.id
           });
           setTimeout(() => n.close(), 8000);
         } catch (e) {
           // Ignore mobile security errors
         }
       }
    }
  }, [alerts, isMuted]);

  const toggleMute = () => {
    const next = !isMuted;
    setIsMuted(next);
    try {
        localStorage.setItem("agency_mute", String(next));
    } catch (e) {}

    if (!next && typeof window !== "undefined" && "Notification" in window) {
        Notification.requestPermission();
    }
  };

  const headerStatus = alerts.some((a) => a.severity === "critical")
    ? { label: "HOT", color: BRAND.yellowHot }
    : alerts.length > 0
    ? { label: "ACTIVE", color: BRAND.green }
    : { label: "CLEAR", color: BRAND.greenDark };

  /* =======================
      RENDER
  ======================= */
  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        display: "grid",
        gridTemplateRows: "auto 1fr",
        borderRadius: 4,
        bgcolor: BRAND.white,
        border: `1px solid ${BRAND.border}`,
        overflow: "hidden"
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          bgcolor: BRAND.greenSoft,
          borderBottom: `1px solid ${BRAND.border}`
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          <RadarIcon sx={{ color: BRAND.green }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography fontWeight={800} fontSize={isMobile ? 14 : 15}>
              AI Coach
            </Typography>
            <Chip
              size="small"
              label={headerStatus.label}
              sx={{
                mt: 0.5,
                height: 18,
                fontSize: 11,
                bgcolor: headerStatus.color,
                color: "#000",
                fontWeight: 700,
                pointerEvents: "none"
              }}
            />
          </Box>
        </Stack>

        <Tooltip title={isMuted ? "Enable alerts" : "Mute alerts"}>
          <IconButton size="small" onClick={toggleMute}>
            {isMuted ? <BellOffIcon /> : <BellOnIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* BODY */}
      <Box sx={{ minHeight: 0, overflowY: "auto" }}>
        {alerts.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <OkIcon sx={{ fontSize: 40, color: BRAND.green, mb: 1 }} />
            <Typography color={BRAND.textMuted}>
              No alerts right now
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            <AnimatePresence>
              {alerts.map((alert) => {
                const isHot =
                  alert.severity === "critical" ||
                  alert.type === "OPPORTUNITY";

                return (
                  <motion.div
                    key={alert.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                  >
                    <ListItem
                      divider
                      onClick={canHandleAlertClick ? () => handleAlertClick(alert) : undefined}
                      sx={{
                        px: 2,
                        py: 1.5,
                        cursor: canHandleAlertClick ? "pointer" : "default",
                        bgcolor: isHot
                          ? `${BRAND.yellowHot}15`
                          : "transparent",
                        "&:hover": { bgcolor: BRAND.greenSoft }
                      }}
                      secondaryAction={
                        <Stack direction="row" spacing={0.5}>
                          {/* Assign Button (Only for Managers/Admins if passed) */}
                          {canHandleAssignClick && isHot && (
                            <Tooltip title="Assign">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAssignClick(alert);
                                }}
                              >
                                <PersonAddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      }
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {isHot ? (
                          <FireIcon sx={{ color: BRAND.yellowHot }} />
                        ) : alert.type === "GHOST" ? (
                          <GhostIcon sx={{ color: BRAND.textMuted }} />
                        ) : alert.severity === "critical" ? (
                          <WarningIcon color="error" />
                        ) : (
                          <AlarmIcon sx={{ color: BRAND.green }} />
                        )}
                      </ListItemIcon>

                      <ListItemText
                        primary={
                          <Typography fontSize={14} fontWeight={600} sx={{ overflowWrap: "anywhere" }}>
                            {alert.message}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color={BRAND.textMuted}>
                            {alert.type}
                          </Typography>
                        }
                      />

                      <ArrowForwardIcon
                        sx={{ fontSize: 16, opacity: 0.4 }}
                      />
                    </ListItem>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </List>
        )}
      </Box>
    </Paper>
  );
}

AICoachWidget.propTypes = {
  inquiries: PropTypes.array,
  hotLeads: PropTypes.array,
  user: PropTypes.object,
  onAlertClick: PropTypes.func,
  onAssignClick: PropTypes.func
};
